(function () {
  if (window.MapboxStoreLocator) return;

  const instances = new Map();
  let libraryPromise;

  function loadLibrary() {
    if (window.mapboxgl) return Promise.resolve(window.mapboxgl);
    if (libraryPromise) return libraryPromise;

    libraryPromise = new Promise(function (resolve, reject) {
      const css = document.createElement('link');
      css.rel = 'stylesheet';
      css.href = 'https://api.mapbox.com/mapbox-gl-js/v3.30.0/mapbox-gl.css';
      document.head.appendChild(css);

      const script = document.createElement('script');
      script.src = 'https://api.mapbox.com/mapbox-gl-js/v3.30.0/mapbox-gl.js';
      script.onload = function () {
        window.mapboxgl ? resolve(window.mapboxgl) : reject(new Error('Mapbox unavailable'));
      };
      script.onerror = function () { reject(new Error('Mapbox unavailable')); };
      document.head.appendChild(script);
    }).catch(function (error) {
      libraryPromise = null;
      throw error;
    });

    return libraryPromise;
  }

  function coordinates(card) {
    const lat = Number(card.dataset.lat);
    const lng = Number(card.dataset.lng);
    if (!card.dataset.lat || !card.dataset.lng ||
        !Number.isFinite(lat) || !Number.isFinite(lng) ||
        Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
    return { lat: lat, lng: lng };
  }

  function distance(lat1, lng1, lat2, lng2) {
    const radians = Math.PI / 180;
    const a = Math.sin((lat2 - lat1) * radians / 2) ** 2 +
      Math.cos(lat1 * radians) * Math.cos(lat2 * radians) *
      Math.sin((lng2 - lng1) * radians / 2) ** 2;
    return 12742 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  function showStatus(root, selector, message) {
    root.querySelector(selector).textContent = message;
  }

  function updateScrollCue(instance) {
    const list = instance.list;
    const hasMore = list.scrollHeight > list.clientHeight + 2 &&
      list.scrollTop + list.clientHeight < list.scrollHeight - 2;
    instance.drawer.dataset.scrollCue = String(hasMore);
  }

  function setDrawerState(instance, state) {
    instance.drawerState = state;
    instance.root.dataset.drawerState = state;
    const mobile = instance.mobileMedia.matches;
    instance.drawerContent.inert = mobile && state !== 'open';
    instance.drawer.setAttribute('aria-hidden', String(mobile && state === 'closed'));
    instance.drawerToggle.setAttribute('aria-expanded', String(mobile && state === 'open'));
    updateScrollCue(instance);
  }

  function focusStore(instance, card, zoom) {
    if (!instance.ready) return;
    const marker = instance.markers.get(card.dataset.storeId);
    if (!marker) return;
    instance.map.flyTo({ center: marker.getLngLat(), zoom: zoom || 15 });
    if (!marker.getPopup().isOpen()) marker.togglePopup();
    if (instance.mobileMedia.matches) setDrawerState(instance, 'peek');
  }

  function filterStores(instance) {
    const city = instance.citySelect.value;
    const visible = [];

    instance.cards.forEach(function (card) {
      const match = city === 'all' || card.dataset.city === city;
      card.hidden = !match;
      const marker = instance.markers.get(card.dataset.storeId);
      if (marker) {
        marker.getElement().style.display = match ? '' : 'none';
        if (!match) marker.getPopup().remove();
        if (match) visible.push(marker);
      }
    });

    updateScrollCue(instance);
    if (!instance.ready) return;
    if (city === 'all') {
      instance.map.flyTo({ center: instance.center, zoom: instance.zoom });
    } else if (visible.length === 1) {
      instance.map.flyTo({ center: visible[0].getLngLat(), zoom: 14 });
    } else if (visible.length > 1) {
      const bounds = new window.mapboxgl.LngLatBounds();
      visible.forEach(function (marker) { bounds.extend(marker.getLngLat()); });
      instance.map.fitBounds(bounds, { padding: 48, maxZoom: 14 });
    }
  }

  function makePopup(card) {
    const content = document.createElement('div');
    content.className = 'map-popup';
    const heading = document.createElement('h3');
    heading.textContent = card.querySelector('.store-card-name').textContent.trim();
    content.appendChild(heading);

    ['[data-store-address]', '[data-store-hours]'].forEach(function (selector) {
      const source = card.querySelector(selector);
      if (!source) return;
      const p = document.createElement('p');
      p.textContent = source.textContent.trim();
      content.appendChild(p);
    });

    const link = card.querySelector('.store-button');
    if (link) {
      const popupLink = link.cloneNode(true);
      popupLink.className = 'store-popup-link';
      content.appendChild(popupLink);
    }
    return content;
  }

  function createMarkers(instance) {
    if (instance.ready) return;
    instance.ready = true;
    const mapElement = instance.root.querySelector('[data-map]');
    const pinImage = mapElement.dataset.pinImage;
    const pinWidth = Math.min(80, Math.max(20, Number(mapElement.dataset.pinWidth) || 40));

    instance.cards.forEach(function (card) {
      const point = coordinates(card);
      if (!point) return;

      const element = document.createElement('div');
      element.className = 'store-map-marker';
      element.setAttribute('aria-label', card.querySelector('.store-card-name').textContent.trim());

      const markerFace = document.createElement('span');
      markerFace.setAttribute('aria-hidden', 'true');

      if (pinImage) {
        element.classList.add('store-map-marker--image');
        element.style.setProperty('--store-marker-size', pinWidth + 'px');

        const image = document.createElement('img');
        image.alt = '';
        image.width = pinWidth;
        image.height = pinWidth;
        image.draggable = false;
        image.addEventListener('error', function () {
          element.classList.remove('store-map-marker--image');
          image.remove();
          element.appendChild(markerFace);
        }, { once: true });

        image.src = pinImage;
        element.appendChild(image);
      } else {
        element.appendChild(markerFace);
      }

      const popup = new window.mapboxgl.Popup({
        offset: 18,
        focusAfterOpen: false
      }).setDOMContent(makePopup(card));

      const marker = new window.mapboxgl.Marker({ element: element, anchor: 'bottom' })
        .setLngLat([point.lng, point.lat])
        .setPopup(popup)
        .addTo(instance.map);

      popup.on('open', function () {
        instance.markers.forEach(function (other) {
          if (other !== marker) other.getPopup().remove();
        });
        instance.cards.forEach(function (item) {
          item.classList.toggle('is-active', item === card);
        });
      });

      popup.on('close', function () { card.classList.remove('is-active'); });
      instance.markers.set(card.dataset.storeId, marker);
    });

    filterStores(instance);
    showStatus(instance.root, '[data-map-status]', '');
    instance.map.resize();
  }

  function createMap(instance) {
    const container = instance.root.querySelector('[data-map]');
    const token = container.dataset.token.trim();
    if (!token) {
      showStatus(instance.root, '[data-map-status]', 'Enter a Mapbox access token in the section settings.');
      return;
    }
    if (!window.mapboxgl.supported()) {
      showStatus(instance.root, '[data-map-status]', 'This browser cannot display the map.');
      return;
    }

    const lat = Number(container.dataset.lat);
    const lng = Number(container.dataset.lng);
    instance.center = [Number.isFinite(lng) ? lng : 31.2357, Number.isFinite(lat) ? lat : 30.0444];
    instance.zoom = Number(container.dataset.zoom) || 12;

    try {
      instance.map = new window.mapboxgl.Map({
        container: container,
        accessToken: token,
        style: container.dataset.style.trim() || 'mapbox://styles/mapbox/streets-v12',
        center: instance.center,
        zoom: instance.zoom
      });
      instance.map.addControl(new window.mapboxgl.NavigationControl({ showCompass: false }));

      const whenStyleReady = function () {
        if (!instance.ready && instance.map.isStyleLoaded()) createMarkers(instance);
      };
      instance.map.on('styledata', whenStyleReady);
      instance.map.on('load', whenStyleReady);
      instance.map.on('error', function (event) {
        if (!instance.ready) {
          showStatus(instance.root, '[data-map-status]',
            'The map style could not load. Check the published Style URL and access token.');
          console.error('Store locator map:', event.error);
        }
      });
    } catch (error) {
      showStatus(instance.root, '[data-map-status]', 'The map could not load. Check its settings.');
      console.error('Store locator map:', error);
    }
  }

  function nearestStores(instance) {
    if (!navigator.geolocation) {
      showStatus(instance.root, '[data-location-status]', 'Location is not supported by this browser.');
      return;
    }
    showStatus(instance.root, '[data-location-status]', 'Finding your location…');
    navigator.geolocation.getCurrentPosition(function (position) {
      if (!instance.root.isConnected) return;
      const lat = position.coords.latitude;
      const lng = position.coords.longitude;
      const sorted = instance.cards.slice().sort(function (a, b) {
        const pointA = coordinates(a);
        const pointB = coordinates(b);
        const aKm = pointA ? distance(lat, lng, pointA.lat, pointA.lng) : Infinity;
        const bKm = pointB ? distance(lat, lng, pointB.lat, pointB.lng) : Infinity;
        return aKm - bKm;
      });
      sorted.forEach(function (card) { instance.list.appendChild(card); });
      instance.cards = sorted;
      instance.list.scrollTop = 0;
      updateScrollCue(instance);
      const nearest = sorted.find(function (card) { return !card.hidden && coordinates(card); });
      showStatus(instance.root, '[data-location-status]',
        nearest ? 'Stores sorted by distance from your location.' : 'No stores with coordinates match this city.');
      if (nearest) focusStore(instance, nearest, 14);
    }, function () {
      if (instance.root.isConnected) {
        showStatus(instance.root, '[data-location-status]', 'Location unavailable. Allow access and try again.');
      }
    }, { enableHighAccuracy: false, timeout: 10000 });
  }

  function destroy(id) {
    const instance = instances.get(id);
    if (!instance) return;
    window.removeEventListener('resize', instance.resizeListener);
    instance.mobileMedia.removeEventListener('change', instance.mediaListener);
    if (instance.map) instance.map.remove();
    instances.delete(id);
  }

  function initialize(root) {
    const id = root.dataset.sectionId;
    const previous = instances.get(id);
    if (previous && previous.root === root) return;
    if (previous) destroy(id);

    const instance = {
      root: root,
      list: root.querySelector('[data-stores-list]'),
      drawer: root.querySelector('[data-drawer]'),
      drawerContent: root.querySelector('[data-drawer-content]'),
      drawerToggle: root.querySelector('[data-drawer-toggle]'),
      mobileMedia: window.matchMedia('(max-width: 990px)'),
      drawerState: 'closed',
      citySelect: root.querySelector('[data-city-filter]'),
      cards: Array.from(root.querySelectorAll('.store-card')),
      markers: new Map(),
      ready: false,
      map: null
    };
    instances.set(id, instance);
    instance.mediaListener = function () {
      setDrawerState(instance, instance.mobileMedia.matches ? 'closed' : 'open');
      if (instance.map) instance.map.resize();
    };
    instance.resizeListener = function () {
      updateScrollCue(instance);
      if (instance.map) instance.map.resize();
    };
    instance.mobileMedia.addEventListener('change', instance.mediaListener);
    window.addEventListener('resize', instance.resizeListener);
    setDrawerState(instance, 'closed');

    const cities = [...new Set(instance.cards.map(function (card) {
      return card.dataset.city.trim();
    }).filter(Boolean))].sort(function (a, b) { return a.localeCompare(b); });
    cities.forEach(function (city) {
      const option = document.createElement('option');
      option.value = city;
      option.textContent = city;
      instance.citySelect.appendChild(option);
    });

    instance.citySelect.addEventListener('change', function () { filterStores(instance); });
    instance.list.addEventListener('scroll', function () { updateScrollCue(instance); }, { passive: true });
    instance.drawerToggle.addEventListener('click', function () {
      setDrawerState(instance, instance.drawerState === 'open' ? 'closed' : 'open');
    });
    root.querySelector('[data-drawer-peek]').addEventListener('click', function () {
      setDrawerState(instance, 'open');
    });
    root.querySelector('[data-map]').addEventListener('pointerdown', function () {
      if (instance.mobileMedia.matches && instance.drawerState !== 'closed') {
        setDrawerState(instance, 'closed');
      }
    });
    root.querySelector('[data-use-location]').addEventListener('click', function () {
      nearestStores(instance);
    });
    instance.list.addEventListener('click', function (event) {
      if (event.target.closest('a')) return;
      const card = event.target.closest('.store-card');
      if (card) focusStore(instance, card);
    });
    window.requestAnimationFrame(function () { updateScrollCue(instance); });

    loadLibrary().then(function () {
      if (root.isConnected && instances.get(id) === instance) createMap(instance);
    }).catch(function () {
      if (root.isConnected) {
        showStatus(root, '[data-map-status]', 'The map library could not load. Please try again.');
      }
    });
  }

  function initializeWithin(scope) {
    if (scope.matches && scope.matches('[data-store-locator]')) initialize(scope);
    scope.querySelectorAll('[data-store-locator]').forEach(initialize);
  }

  window.MapboxStoreLocator = { initialize: initializeWithin };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initializeWithin(document); });
  } else {
    initializeWithin(document);
  }
  document.addEventListener('shopify:section:load', function (event) {
    initializeWithin(event.target);
  });
  document.addEventListener('shopify:section:unload', function (event) {
    const root = event.target.matches('[data-store-locator]') ?
      event.target : event.target.querySelector('[data-store-locator]');
    if (root) destroy(root.dataset.sectionId);
  });
}());